export type GroupTextTone = 'light' | 'dark';

// PSTUDIO-3393: fixed palette of 8 preset swatches for the group-row color
// picker. Order matches design spec (Figma RhFJR0pxKC5X12geakgi2H, node
// 5139:75257).
// PSTUDIO-4122: moved here from `draggable-row.tsx`, which cannot be imported
// by a pro-studio unit test — under `vitest.config.ts` (`environment: 'node'`)
// it throws `TypeError: d.createTreeWalker is not a function` out of `lit-html`
// via `packages/design-system/src/components/global-styles.ts`. The CSV import
// path needs the same default the renderer falls back to, and a second copy of
// the hex would be free to drift from this one.
export const GROUP_COLOR_PRESETS = [
  '#62697C',
  '#151A25',
  '#8573E7',
  '#004DDB',
  '#B841B6',
  '#34785A',
  '#FFA800',
  '#A13337',
] as const;

// PSTUDIO-3393: default group color = the FIRST preset (a gray). A group with no
// explicitly chosen color renders in this, so all groups look identical and the
// user only ever *changes* the color.
//
// It is a render-time fallback for a group the USER created: `groupRows` writes
// no color at all, so the store holds `groupColor` unset and the renderer's `??`
// supplies this. A group rebuilt from a CSV is the one case where the value is
// not absent but EMPTY (`''`), which no `??` can catch — see
// `resolveImportedGroupColor` in `utils/csv-grouping/csv-group-meta.ts`.
export const DEFAULT_GROUP_COLOR = GROUP_COLOR_PRESETS[0];

/**
 * Parse a hex color string into 8-bit RGB. Accepts 3/6/8-digit hex, with or
 * without a leading '#', any case. Alpha (8-digit) is ignored — tone is judged
 * on the visible RGB. Returns null for anything it can't parse so callers fall
 * back to the neutral tone.
 */
export const parseHexColor = (
  hex: string
): { r: number; g: number; b: number } | null => {
  if (typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(h)) {
    h = h
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (!/^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(h)) return null;
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

/** Relative luminance (WCAG 2.x, sRGB → linear), 0 (black) … 1 (white). */
const relativeLuminance = ({
  r,
  g,
  b,
}: {
  r: number;
  g: number;
  b: number;
}): number => {
  const channel = (c: number): number => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

/**
 * Decide whether light (white) or dark text is readable on a group-row's
 * background color. Compares the WCAG contrast RATIO of white text vs black
 * text against this background and picks whichever is higher — so mid-luminance
 * colors (where a fixed 0.5 threshold is ambiguous) still get the more legible
 * option, and the text can never blend into a background close to its own
 * color. Empty / invalid input → 'dark' (the neutral uncolored band uses the
 * normal dark text).
 */
export const getGroupRowTextTone = (bg?: string | null): GroupTextTone => {
  if (!bg) return 'dark';
  const rgb = parseHexColor(bg);
  if (!rgb) return 'dark';
  const lum = relativeLuminance(rgb);
  // Contrast ratio = (L_lighter + 0.05) / (L_darker + 0.05); white L=1, black L=0.
  const contrastWithWhite = 1.05 / (lum + 0.05);
  const contrastWithBlack = (lum + 0.05) / 0.05;
  return contrastWithWhite >= contrastWithBlack ? 'light' : 'dark';
};

/**
 * True when `color` is a real CUSTOM group color — one that is set but is NOT
 * one of the provided preset swatches (compared case-insensitively). This drives
 * the checkmark shown on the rainbow "Custom" swatch: it marks the custom option
 * as selected only when the active color came from the freeform picker, not from
 * clicking one of the 8 presets. Empty / absent color → false (nothing custom
 * selected). PSTUDIO-3393.
 */
export const isCustomGroupColor = (
  color: string | null | undefined,
  presets: readonly string[]
): boolean => {
  if (!color) return false;
  const c = color.toUpperCase();
  return !presets.some((hex) => hex.toUpperCase() === c);
};
